<script setup lang="ts">

import { Direction } from '../fin/optimisation';
import MontecarloRetirementOutput from './MontecarloRetirementOutput.vue';
import { ref, Ref } from 'vue';
import { RetirementConfig } from '../fin/retirement_config';
import RetirementConfigElem from './RetirementConfigElem.vue';
import { RetirementState } from '../fin/retirement_state';
import { MultipleHistory } from '../fin/retirement';
import { TimeSeriesS } from '../fin/history';
import { Quantiles, STANDARD_QUANTILES } from '../fin/quantile';
// import { onVisible } from './observability';
import * as wc from '../worker/client.ts';
import * as ww from '../worker/worker.ts';

interface Loadable {
  load(): void;
}

const retirementConfigElemRef = ref<Loadable|null>(null);
const montecarloRetirementOutputRef = ref<InstanceType<typeof MontecarloRetirementOutput>|null>(null);
const earlyPensionWithdrawalGuidance = ref<Direction>(Direction.NO_GUIDANCE);
const postRetirementIsaSavingGuidance = ref<Direction>(Direction.NO_GUIDANCE);
let latestConfig: Ref<RetirementConfig|void> = ref<RetirementConfig|void>();
const exhausted: Ref<string> = ref<string>('');
const exhaustionYear: Ref<string> = ref<string>('');
const exhaustionAge: Ref<string> = ref<string>('');

let assetTimeQuantilesSeries: Ref<TimeSeriesS<Quantiles>> = ref(new TimeSeriesS<Quantiles>([]));
let birthYear: Ref<number> = ref<number>(0);
const retirementYear: Ref<number> = ref<number>(0);
const maxYear: Ref<number> = ref<number>(0);
const calculationCompletionRatio: Ref<number> = ref<number>(0);
let currentCalculationTaskId = '';
const emit = defineEmits(['configChanged']);

function calculate(config: RetirementConfig): void {
  console.log('starting MontecarloRetirement.vue calculate()');
  latestConfig.value = config;
  retirementConfigElemRef.value?.load();

  const pre_retirement_rs: RetirementState = new RetirementState(
    config.person.simulation_start_year,
    config.person,
    config.initialAssets,
    config.market,
    [],
  );

  if (currentCalculationTaskId !== '') {
    wc.cancelTask(currentCalculationTaskId);
  }

  currentCalculationTaskId = wc.requestMonteCarloRetirement(
    config,
    {
      completionHandler: (
        function(r: ww.TaskResponseMessage) {
          console.log('got worker task completion message');
          currentCalculationTaskId = '';
          const atqs = MultipleHistory.deserialize(r.serializedResult)
          assetTimeQuantilesSeries.value = atqs.histories;
          birthYear.value = config.person.birth_date.getUTCFullYear();
          retirementYear.value = pre_retirement_rs.person.retirement_date.getUTCFullYear();
          maxYear.value = atqs.maxYear;
          calculationCompletionRatio.value = 1.0;

          const exhaustionYearNum = atqs.exhaustionYears.get(
            config.person.critical_quantile_key) ?? 0;

          const exhaustedBool
            = exhaustionYearNum < config.person.max_age + maxYear.value;

          const exhaustionAgeNum = exhaustedBool ?
            exhaustionYearNum - birthYear.value :
            0;

          if (exhaustedBool) {
            exhausted.value = "yes";
            exhaustionYear.value = exhaustionYearNum.toString();
            exhaustionAge.value = exhaustionAgeNum.toString();
          } else {
            exhausted.value = "no";
            exhaustionYear.value = '';
            exhaustionAge.value = '';
          }
        }
      ),
      failureHandler: () => console.warn('got worker task failure message'),
      progressHandler: (progress: number) => {
        console.debug(`got worker task progress message: ${progress}`);
        calculationCompletionRatio.value = progress;
      },
    } as wc.Listener,
  );
}

function redraw(): void {
  if (montecarloRetirementOutputRef.value) {
    montecarloRetirementOutputRef.value.redraw();
  }
}

defineExpose({ calculate, redraw });

</script>

<template>
  <div activityName="MontecarloRetirement">
    <h2>MontecarloRetirement</h2>
    <div class="explainer-card">
      Explore multiple retirements using randomly varying market conditions
      according to configurable statistical properties, so as to explore the
      likelihood of running out of money early.
      <br/>
      This tool is the best way to investigate scheduling risk: The chance that
      an unlucky few years at the beginning could have disproportionate impact
      on the rest of your retirement.
    </div>
    <RetirementConfigElem
      ref="retirementConfigElemRef"
      :quantileKeys="[...STANDARD_QUANTILES]"
      @configChanged="(cfg) => {calculate(cfg); emit('configChanged', cfg);}"
      :showMarketUncertainty="true"
      :earlyPensionWithdrawalGuidance="earlyPensionWithdrawalGuidance"
      :postRetirementIsaSavingGuidance="postRetirementIsaSavingGuidance"
    />
    <div>
      <table>
        <tbody>
          <tr v-on:click="() => calculate(latestConfig!)">
            <td>Calculation progress</td>
            <td>{{Math.round(calculationCompletionRatio * 100)}}%</td>
          </tr>
        </tbody>
      </table>
    </div>
    <MontecarloRetirementOutput
      ref="montecarloRetirementOutputRef"
      :calculationCompletionRatio="calculationCompletionRatio"
      :assetTimeQuantilesSeriess="assetTimeQuantilesSeries"
      :criticalQuantileKey="latestConfig?.person.critical_quantile_key ?? 0.05"
      :birthYear="birthYear"
      :retirementYear="retirementYear"
      :maxYear="maxYear"
      :exhausted="exhausted"
      :exhaustionYear="exhaustionYear"
      :exhaustionAge="exhaustionAge"
    />
  </div>
</template>

<style scoped>
</style>
